import { assign, cloneDeep } from 'lodash-es'
import { type LoginStatus, PermissionRole, UserAccountType, type UserAuthState } from '@/store/modules/auth/type'
import { getPermissionGroup, getRdxToken, loginTgc, logout as userLogout } from '@/api/auth'
import { clearSimulated } from '@/utils/simulated'
import { clearCsToken, clearCustomerId, clearToken, setToken } from '@/utils/auth'
import { saveCookie } from '@/utils/cache'
import { COOKIE_JESSSION_ID_KEY } from '@/constant/cache-key'
import { i18n } from '@/plugins/i18n'
import useRequest from '@/composables/use-request'
import store from '@/store'
import { LOGIN_SUCCESS } from '@/constant/event-name-key'
import { setUserId, trackPageView } from '@/directives/track/utils'

const { get } = useRequest()

const defaultUser: UserAuthState = {
  name: '',
  avatar: '',
  email: '',
  current_enterprise: '',
  permissionGroup: [],
  refresh_token: '',
  user_name: '',
  nick_name: '',
  tenant_id: '',
  user_id: '',
  dept_id: [],
  role_id: [],
  role_name: [],
  enterprise_id: [],
  account: '',
  access_token: '', // 鉴权的token
  token_type: '',
  expires_in: 0,
  license: '',
  simulated_login_flag: 0,
  simulated_login_ticket: '',
  current_enterprise_name: '',
  current_enterprise_name_cn: '',
  is_admin: false,
  highest_authority_organization_id: '',
  role_permission: PermissionRole.Guest,
  user_account_types: [],
}

const useAuthStore = defineStore('auth', () => {
  const user = ref<UserAuthState>(cloneDeep(defaultUser))
  const loginStatus = ref<LoginStatus>(0)
  // 是否是超级管理员
  const superAdmin = computed(() => user.value.user_id === 'SssUser0001')

  // 权限缓存，避免重复计算
  const permissionCache = computed(() => {
    const types = user.value.user_account_types
    return {
      types,
      length: types.length,
      hasSuper: types.includes(UserAccountType.SuperAdmin),
      hasBroker: types.includes(UserAccountType.Broker),
      hasCustomer: types.includes(UserAccountType.Customer),
      hasCustomerService: types.includes(UserAccountType.CustomerService),
    }
  })

  // 包含权限检查
  const includeSuperView = computed(() => permissionCache.value.hasSuper)
  const includeBrokerView = computed(() => permissionCache.value.hasBroker)
  const includeCustomerView = computed(() => permissionCache.value.hasCustomer)
  const includeCustomerServiceView = computed(() => permissionCache.value.hasCustomerService)

  // 单一权限检查 - 优化版本
  const onlySuperView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.SuperAdmin
  })
  const onlyBrokerView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Broker
  })
  const onlyCustomerView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Customer
  })
  const onlyCustomerServiceView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.CustomerService
  })

  // 隐藏佣金模块 - 优化版本
  const hiddenCommissionModule = computed(() => {
    const cache = permissionCache.value

    // 只有客户权限
    if (cache.length === 1 && cache.hasCustomer) {
      return true
    }

    // 客户和客服权限的组合
    if (cache.length === 2 && cache.hasCustomer && cache.hasCustomerService) {
      return true
    }

    return false
  })

  function setInfo(newUser: Partial<UserAuthState>) {
    user.value = assign({}, user.value, newUser)
  }

  function resetInfo() {
    user.value = cloneDeep(defaultUser)
  }

  function setLoginStatus(status: LoginStatus) {
    loginStatus.value = status
  }

  async function loadUser() {
    const { emit } = store.evenStore
    // try {
    // const { setRole } = store.permissionStore
    setLoginStatus(1)
    const { data } = await getRdxToken<UserAuthState>()
    if (data && data?.user_id) {
      const { data: permissionGroup } = await getPermissionGroup(data.user_id)
      setInfo(data)
      setToken(data.access_token ?? '')
      setInfo({ permissionGroup })
      const info = data.email ?? `${data.nick_name}(${data.user_id})`
      // matomo 设置用户信息
      setUserId(info)
      trackPageView()

      // setRole(data.role_permission, superAdmin.value)
      setLoginStatus(2)
      emit(LOGIN_SUCCESS, { isCheck: true })
    }
    else {
      setLoginStatus(3)
    }
    // }
    // catch (error) {
    //   console.error('loadUser error :>>', error)
    // }
  }

  async function renewalRoles() {
    // try {
    const { content } = await loginTgc<{ redirectUrl: string }>()
    if (content) {
      const data = await get<string>(content?.redirectUrl)
      if (data && data.content) {
        saveCookie(COOKIE_JESSSION_ID_KEY, data.content, { path: '/' })
      }
    }
    // }
    // catch (error) {
    //   console.error('renewalRoles error :>>', error)
    // }
  }

  function logoutCallBack() {
    // const store = useAppStore();
    resetInfo()
    clearToken()
    clearCsToken()
    clearCustomerId()
    clearSimulated()
    // removeRouteListener();
    // removeModalListener();
    // store.clearServerMenu();
  }

  async function logout() {
    try {
      logoutCallBack()
      const data = await userLogout()
      if (data && data.content) {
        const res = `${data.content}&lang=${i18n.global.locale.value || 'en-US'}`
        window.location.href = res
      }
    }
    catch (error) {
      console.error('logout error', error)
    }
  }

  return {
    user,
    loginStatus,
    superAdmin,
    includeSuperView,
    includeBrokerView,
    includeCustomerServiceView,
    includeCustomerView,
    hiddenCommissionModule,
    onlySuperView,
    onlyBrokerView,
    onlyCustomerView,
    onlyCustomerServiceView,

    setInfo,
    resetInfo,
    loadUser,
    renewalRoles,
    logoutCallBack,
    logout,
    setLoginStatus,
  }
}, {
  // https://prazdevs.github.io/pinia-plugin-persistedstate/zh/guide/config.html
  persist: {
    key: 'auth',
    paths: ['user'],
  },
})

export default useAuthStore
